import { arrayUtils, timeUtils } from '@sato/components';

import { LanguageKey } from '@models/Language';
import { TranslatorFn } from '@stores/localization';

import { WaterUsageChartDataPoint } from './WaterUsageChart';

type Props = {
  waterUsageData: WaterUsageChartDataPoint;
  t: TranslatorFn;
  locale: LanguageKey;
};

/**
 * Marks where the bold-worthy part of a tooltip line (the amount) begins.
 * A NUL character is used because it can't appear in translated text,
 * so it's safe to split on.
 */
export const BOLD_AMOUNT_MARKER = String.fromCharCode(0);

export const getChartTooltipText = ({ waterUsageData, t, locale }: Props) => {
  if (!waterUsageData.timeStamp) {
    return t('waterUsageNoResults');
  }

  const formatTimestamp = (date: Date | undefined) =>
    date
      ? timeUtils.formatDate({
          date,
          dateFormat: timeUtils.DATE_FORMATS.DATE_AND_TIME_SHORT,
          locale,
        })
      : undefined;

  const formatRange = (
    range: { timeStamp?: Date; previousTimestamp?: Date } | undefined
  ) =>
    range &&
    arrayUtils.joinArrayString(
      [
        formatTimestamp(range.previousTimestamp),
        formatTimestamp(range.timeStamp),
      ],
      ' – '
    );

  // Line order/count here must stay in sync with the style/lineHeight
  // arrays on the VictoryLabel in WaterUsageChart.tsx
  return [
    `${t('waterUsageWarmWaterLabel')} ${BOLD_AMOUNT_MARKER}${waterUsageData.usage.warm} ${t('liters')}`,
    formatRange({
      timeStamp: waterUsageData.usage.timeStampWarm,
      previousTimestamp: waterUsageData.usage.previousTimeStampWarm,
    }),
    `${t('waterUsageColdWaterLabel')} ${BOLD_AMOUNT_MARKER}${waterUsageData.usage.cold} ${t('liters')}`,
    formatRange({
      timeStamp: waterUsageData.usage.timeStampCold,
      previousTimestamp: waterUsageData.usage.previousTimeStampCold,
    }),
    `${t('waterUsageTotalWaterLabel')}: ${waterUsageData.usage.total} ${t('liters')}`,
  ].join('\n');
};
